import { useQuery, useMutation } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';
import { TestReason } from '../constants/test-reason';
import { Question } from '../interfaces/course';
import type { TestBlockedReason } from '../constants/test-blocked-reason';
import type { TestPeriod } from '../constants/test-period';

export interface TestAccess {
  limitsEnabled: boolean;
  canAccess: boolean;
  canStart: boolean;
  reason: TestBlockedReason | null;
  testsCompleted: number;
  maxTests: number;
  minTests: number;
  period: TestPeriod;
}

export interface TestAttempt {
  question_id: number;
  level_id: number;
  course_id: number;
  is_correct: boolean;
  answered_at: string;
}

export interface CompleteTestRequest {
  attempts: TestAttempt[];
  reason: TestReason;
}

interface StartTestResponse {
  sessionId: number;
  startedAt: string;
  timeLimitSeconds: number | null;
  questions: Question[];
}

async function fetchTestAccess(organisationId: number): Promise<TestAccess> {
  const response = await apiFetch(ENDPOINTS.TEST_ACCESS(organisationId));

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch test access');
  }

  return response.json();
}

export function useTestAccess(organisationId: number) {
  return useQuery({
    queryKey: ['test', 'access', organisationId],
    queryFn: () => fetchTestAccess(organisationId),
    enabled: !!organisationId,
  });
}

async function startTest(organisationId: number): Promise<StartTestResponse> {
  const response = await apiFetch(ENDPOINTS.TEST_START, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ organisationId }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to start test');
  }

  return response.json();
}

export function useStartTest() {
  return useMutation({
    mutationFn: (organisationId: number) => startTest(organisationId),
  });
}

async function completeTest(
  sessionId: number,
  data: CompleteTestRequest,
): Promise<{ success: boolean }> {
  const response = await apiFetch(ENDPOINTS.TEST_COMPLETE(sessionId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to complete test');
  }

  return response.json();
}

export function useCompleteTest(sessionId: number) {
  return useMutation({
    mutationFn: (data: CompleteTestRequest) => completeTest(sessionId, data),
  });
}
